import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraduationCap, ScanBarcode, ShieldAlert, TriangleAlert, Undo2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { DueBadge } from '../components/badges';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import type { BulkCheckinResult, BulkCheckoutResult, GradeOptions, ReturnsList, RolloutPreview, RolloutStatus } from '../lib/checkouts';
import { CONDITION_LABEL, dueDateInputToIso, formatDate, personName, toDateInput } from '../lib/format';
import { endOfSchoolYear, invalidateInventory, useCategories, useUrlState } from '../lib/hooks';
import { gradeLabel } from '../lib/oneroster';
import type { AssetCondition, PersonSummary } from '../lib/types';

const DEFAULTS = { tab: 'rollout', cohort: '', categoryId: '', modelId: '', skip: 'CATEGORY', who: 'STUDENTS' };
type Filters = typeof DEFAULTS;
type SetFilters = (patch: Partial<Filters>) => void;
type Tab = 'rollout' | 'returns';

const STATUS: Record<RolloutStatus, { label: string; tone: 'green' | 'gray' | 'red' | 'amber' }> = {
  READY: { label: 'Ready', tone: 'green' },
  HAS_DEVICE: { label: 'Already has one', tone: 'gray' },
  BLOCKED: { label: 'Blocked', tone: 'red' },
  NO_DEVICE: { label: 'No device left', tone: 'amber' },
};

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** The class ("year:2030") or grade ("grade:9") as the API's query. */
function cohortQuery(cohort: string) {
  if (cohort.startsWith('year:')) return { gradYear: cohort.slice(5) };
  if (cohort.startsWith('grade:')) return { gradeLevel: cohort.slice(6) };
  return {};
}

const classOf = (person: Pick<PersonSummary, 'gradYear' | 'gradeLevel'>) => (person.gradYear ? `Class of ${person.gradYear}` : gradeLabel(person.gradeLevel));

function CohortSelect({ value, onChange, grades, emptyLabel }: { value: string; onChange(value: string): void; grades: GradeOptions | undefined; emptyLabel: string }) {
  return (
    <Select value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">{emptyLabel}</option>
      {!!grades?.classYears.length && (
        <optgroup label="Class year">
          {grades.classYears.map((row) => (
            <option key={row.gradYear} value={`year:${row.gradYear}`}>
              Class of {row.gradYear} ({row.students})
            </option>
          ))}
        </optgroup>
      )}
      {!!grades?.grades.length && (
        <optgroup label="Grade">
          {grades.grades.map((row) => (
            <option key={row.gradeLevel} value={`grade:${row.gradeLevel}`}>
              {gradeLabel(row.gradeLevel)} ({row.students})
            </option>
          ))}
        </optgroup>
      )}
    </Select>
  );
}

export function CheckoutBulkPage() {
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const grades = useQuery({ queryKey: ['checkout-grades'], queryFn: () => api.get<GradeOptions>('/checkouts/grades') });
  const tab: Tab = filters.tab === 'returns' ? 'returns' : 'rollout';

  return (
    <>
      <PageHeader
        back={{ to: '/checkouts', label: 'Checkouts' }}
        title="Rollouts & returns"
        description="Hand out devices to a whole grade at once, and collect them again at the end of the year."
      />
      <Tabs<Tab>
        className="mb-6"
        value={tab}
        onChange={(value) => setFilters({ tab: value })}
        tabs={[
          { value: 'rollout', label: 'Grade rollout' },
          { value: 'returns', label: 'End-of-year returns' },
        ]}
      />
      {grades.error ? (
        <ErrorState error={grades.error} onRetry={() => grades.refetch()} />
      ) : tab === 'rollout' ? (
        <Rollout filters={filters} setFilters={setFilters} grades={grades.data} />
      ) : (
        <Returns filters={filters} setFilters={setFilters} grades={grades.data} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Grade rollout
// ---------------------------------------------------------------------------

function Rollout({ filters, setFilters, grades }: { filters: Filters; setFilters: SetFilters; grades: GradeOptions | undefined }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dueDate, setDueDate] = useState(() => toDateInput(endOfSchoolYear()));
  const [notes, setNotes] = useState('');
  const [leftOut, setLeftOut] = useState<Set<string>>(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<BulkCheckoutResult | null>(null);

  const preview = useQuery({
    queryKey: ['checkout-rollout', filters.cohort, filters.categoryId, filters.modelId, filters.skip],
    queryFn: () =>
      api.get<RolloutPreview>('/checkouts/rollout', { ...cohortQuery(filters.cohort), categoryId: filters.categoryId, modelId: filters.modelId, skip: filters.skip }),
    enabled: !!filters.cohort,
  });
  useEffect(() => {
    setLeftOut(new Set());
    setResult(null);
  }, [filters.cohort, filters.categoryId, filters.modelId, filters.skip]);

  const rows = preview.data?.rows ?? [];
  const going = rows.filter((row) => row.status === 'READY' && row.asset && !leftOut.has(row.person.id));
  const warned = going.filter((row) => row.alerts.length > 0).length;
  const models = (grades?.models ?? []).filter((model) => !filters.categoryId || model.categoryId === filters.categoryId);
  const names = new Map(rows.map((row) => [row.person.id, personName(row.person)]));
  const summary = preview.data?.summary;

  const handOut = useMutation({
    mutationFn: () =>
      api.post<BulkCheckoutResult>('/checkouts/bulk', {
        pairs: going.map((row) => ({ personId: row.person.id, assetId: row.asset!.id })),
        dueAt: dueDateInputToIso(dueDate),
        notes: notes || undefined,
        label: preview.data?.label,
      }),
    onSuccess: (data) => {
      invalidateInventory(queryClient);
      setConfirming(false);
      setResult(data);
      toast.success(`Checked out ${plural(data.checkedOut.length, 'device')}`);
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(errorMessage(err));
    },
  });

  const toggle = (personId: string) =>
    setLeftOut((current) => {
      const next = new Set(current);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Who gets what"
          description="Students are paired in name order with devices on the shelf, in asset tag order. Loaners and devices kept on carts aren’t handed out."
        />
        <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Class or grade" required>
            <CohortSelect value={filters.cohort} onChange={(cohort) => setFilters({ cohort })} grades={grades} emptyLabel="Choose a class or grade" />
          </Field>
          <Field label="Devices" hint={grades ? `${grades.available} on the shelf in all` : undefined}>
            <Select value={filters.categoryId} onChange={(event) => setFilters({ categoryId: event.target.value, modelId: '' })}>
              <option value="">Any category</option>
              {grades?.categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.available})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Model">
            <Select value={filters.modelId} onChange={(event) => setFilters({ modelId: event.target.value })}>
              <option value="">Any model</option>
              {models.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} ({model.available})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Leave out students who">
            <Select value={filters.skip} onChange={(event) => setFilters({ skip: event.target.value })}>
              <option value="CATEGORY">Already have one of these</option>
              <option value="ANY">Have any device out</option>
              <option value="NONE">Leave nobody out</option>
            </Select>
          </Field>
          <Field label="Due back" className="sm:col-span-2" hint={dueDate ? `Due ${formatDate(dueDateInputToIso(dueDate))}` : 'No due date'}>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} className="w-44" />
              <Button size="sm" variant="ghost" onClick={() => setDueDate(toDateInput(endOfSchoolYear()))}>
                End of school year
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDueDate('')}>
                No due date
              </Button>
            </div>
          </Field>
          <Field label="Notes" className="sm:col-span-2">
            <Input value={notes} maxLength={2000} onChange={(event) => setNotes(event.target.value)} placeholder="e.g. Fall rollout, charger and case included" />
          </Field>
        </div>
      </Card>

      {result && (
        <div className="space-y-3">
          {result.checkedOut.length > 0 && (
            <Alert tone="green" title={`Checked out ${plural(result.checkedOut.length, 'device')}`}>
              They show on the Checkouts page and on each student’s page.
            </Alert>
          )}
          {result.failed.length > 0 && (
            <Alert tone="red" title={`${result.failed.length} couldn’t be checked out`}>
              <ul className="mt-1 space-y-0.5">
                {result.failed.map((row) => (
                  <li key={row.personId}>
                    {names.get(row.personId) ?? 'A student'}: {row.error}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
        </div>
      )}

      {!filters.cohort ? (
        <Card>
          <EmptyState icon={<GraduationCap />} title="Choose a class or grade" description="You’ll see every student and the device they’ll get before anything is checked out." />
        </Card>
      ) : preview.isLoading ? (
        <Card className="flex h-60 items-center justify-center">
          <Spinner />
        </Card>
      ) : preview.error || !preview.data || !summary ? (
        <Card>
          <ErrorState error={preview.error} onRetry={() => preview.refetch()} />
        </Card>
      ) : (
        <Card>
          <CardHeader
            title={preview.data.label.charAt(0).toUpperCase() + preview.data.label.slice(1)}
            description={`${plural(summary.students, 'student')} · ${summary.ready} ready · ${summary.hasDevice} already have one · ${summary.blocked} blocked · ${summary.noDevice} without a device · ${summary.available} on the shelf`}
            actions={
              <Button variant="primary" disabled={going.length === 0} onClick={() => setConfirming(true)}>
                Check out {plural(going.length, 'device')}
              </Button>
            }
          />
          {preview.data.truncated && (
            <Alert tone="amber" className="m-4">
              Only the first 2,000 students are shown. Choose a smaller group.
            </Alert>
          )}
          {rows.length === 0 ? (
            <EmptyState title="No active students in this group" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th className="w-10">
                    <span className="sr-only">Include</span>
                  </Th>
                  <Th>Student</Th>
                  <Th>Status</Th>
                  <Th>Device</Th>
                  <Th>Notes</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((row) => {
                  const ready = row.status === 'READY';
                  const included = ready && !leftOut.has(row.person.id);
                  return (
                    <tr key={row.person.id} className={ready && !included ? 'bg-slate-50/80 opacity-60' : undefined}>
                      <Td>
                        {ready && (
                          <input
                            type="checkbox"
                            checked={included}
                            onChange={() => toggle(row.person.id)}
                            className="size-4 rounded border-slate-300"
                            aria-label={`Include ${personName(row.person)}`}
                          />
                        )}
                      </Td>
                      <Td>
                        <Link to={`/people/${row.person.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                          {personName(row.person)}
                        </Link>
                        <p className="text-xs text-slate-500">{[classOf(row.person), row.person.email].filter(Boolean).join(' · ')}</p>
                      </Td>
                      <Td>
                        <Badge tone={STATUS[row.status].tone}>{STATUS[row.status].label}</Badge>
                      </Td>
                      <Td>
                        {row.asset ? (
                          <>
                            <span className="font-mono text-xs font-semibold text-brand-800">{row.asset.assetTag}</span>
                            <p className="text-xs text-slate-500">{row.asset.name}</p>
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </Td>
                      <Td className="max-w-sm text-xs">
                        {row.holding.length > 0 && <p className="text-slate-500">Has {row.holding.join(', ')}</p>}
                        {row.alerts.map((alert) => (
                          <p key={alert.key} className={alert.level === 'BLOCK' ? 'text-red-700' : 'text-amber-700'}>
                            {alert.level === 'BLOCK' ? <ShieldAlert className="mr-1 inline size-3" /> : <TriangleAlert className="mr-1 inline size-3" />}
                            {alert.title}
                          </p>
                        ))}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => handOut.mutate()}
        loading={handOut.isPending}
        tone="primary"
        confirmLabel="Check out"
        title={`Check out ${plural(going.length, 'device')}?`}
        description={
          <>
            <p>
              Each student gets the device next to their name{dueDate ? `, due back ${formatDate(dueDateInputToIso(dueDate))}` : ''}.
            </p>
            {warned > 0 && <p className="mt-2 text-amber-700">{warned} of them have warnings. Untick anyone who shouldn’t get a device.</p>}
          </>
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// End-of-year returns
// ---------------------------------------------------------------------------

function Returns({ filters, setFilters, grades }: { filters: Filters; setFilters: SetFilters; grades: GradeOptions | undefined }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const categories = useCategories();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [scan, setScan] = useState('');
  const [scanned, setScanned] = useState<{ ok: boolean; text: string } | null>(null);
  const [condition, setCondition] = useState<AssetCondition | ''>('');
  const [assetStatus, setAssetStatus] = useState('AVAILABLE');
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<BulkCheckinResult | null>(null);

  const list = useQuery({
    queryKey: ['checkout-returns', filters.cohort, filters.categoryId, filters.who],
    queryFn: () => api.get<ReturnsList>('/checkouts/returns', { ...cohortQuery(filters.cohort), categoryId: filters.categoryId, who: filters.who }),
  });
  useEffect(() => {
    setSelected(new Set());
    setScanned(null);
  }, [filters.cohort, filters.categoryId, filters.who]);

  const items = list.data?.items ?? [];
  const allSelected = items.length > 0 && items.every((item) => selected.has(item.id));
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onScan = (event: FormEvent) => {
    event.preventDefault();
    const code = scan.trim();
    if (!code) return;
    setScan('');
    const match = items.find(
      (item) => item.asset.assetTag.toLowerCase() === code.toLowerCase() || item.asset.serialNumber?.toLowerCase() === code.toLowerCase(),
    );
    if (!match) {
      setScanned({ ok: false, text: `${code} isn’t checked out to anyone on this list` });
      return;
    }
    setSelected((current) => new Set(current).add(match.id));
    setScanned({ ok: true, text: `${match.asset.assetTag} from ${match.assigneeLabel}` });
  };

  const collect = useMutation({
    mutationFn: () =>
      api.post<BulkCheckinResult>('/checkouts/bulk/checkin', {
        checkoutIds: [...selected],
        condition: condition || undefined,
        assetStatus,
        notes: notes || undefined,
        label: list.data?.label,
      }),
    onSuccess: (data) => {
      invalidateInventory(queryClient);
      setConfirming(false);
      setResult(data);
      setSelected(new Set());
      toast.success(`Checked in ${plural(data.checkedIn.length, 'device')}`);
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(errorMessage(err));
    },
  });
  const late = result?.checkedIn.filter((row) => row.daysLate !== null).length ?? 0;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="h-fit lg:col-span-2">
        <div className="grid gap-3 border-b border-slate-100 p-4 sm:grid-cols-3">
          <CohortSelect value={filters.cohort} onChange={(cohort) => setFilters({ cohort })} grades={grades} emptyLabel="Every class and grade" />
          <Select value={filters.categoryId} onChange={(event) => setFilters({ categoryId: event.target.value })}>
            <option value="">Any category</option>
            {categories.data?.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
          <Select value={filters.who} onChange={(event) => setFilters({ who: event.target.value })}>
            <option value="STUDENTS">Students</option>
            <option value="EVERYONE">Everyone with a device out</option>
          </Select>
        </div>
        {list.isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner />
          </div>
        ) : list.error || !list.data ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState icon={<Undo2 />} title="Nothing to collect" description="Nothing is checked out to this group." />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm text-slate-600">
              <span>
                {list.data.total} still out
                {list.data.overdue > 0 && <span className="text-red-600"> · {list.data.overdue} overdue</span>} · {selected.size} selected
              </span>
              <Button size="sm" variant="ghost" onClick={() => setSelected(allSelected ? new Set() : new Set(items.map((item) => item.id)))}>
                {allSelected ? 'Clear selection' : 'Select all'}
              </Button>
            </div>
            <Table>
              <thead>
                <tr>
                  <Th className="w-10">
                    <span className="sr-only">Return</span>
                  </Th>
                  <Th>Device</Th>
                  <Th>Borrower</Th>
                  <Th>Checked out</Th>
                  <Th>Due</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {items.map((item) => (
                  <tr key={item.id} className={selected.has(item.id) ? 'bg-brand-50/50' : undefined}>
                    <Td>
                      <input
                        type="checkbox"
                        checked={selected.has(item.id)}
                        onChange={() => toggle(item.id)}
                        className="size-4 rounded border-slate-300"
                        aria-label={`Return ${item.asset.assetTag}`}
                      />
                    </Td>
                    <Td>
                      <Link to={`/assets/${item.asset.id}`} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                        {item.asset.assetTag}
                      </Link>
                      <p className="text-xs text-slate-500">{item.asset.name}</p>
                    </Td>
                    <Td>
                      {item.personId ? (
                        <Link to={`/people/${item.personId}`} className="font-medium text-slate-900 hover:text-brand-700">
                          {item.assigneeLabel}
                        </Link>
                      ) : (
                        item.assigneeLabel
                      )}
                      {item.person && <p className="text-xs text-slate-500">{classOf(item.person)}</p>}
                    </Td>
                    <Td className="whitespace-nowrap">{formatDate(item.checkedOutAt)}</Td>
                    <Td className="whitespace-nowrap">
                      <DueBadge dueAt={item.dueAt} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {list.data.truncated && <p className="px-4 py-3 text-xs text-slate-500">Showing the first 2,000. Check these in and the rest appear.</p>}
          </>
        )}
      </Card>

      <div className="space-y-6">
        <Card className="p-4">
          <form onSubmit={onScan}>
            <Field label="Scan to select">
              <div className="relative">
                <ScanBarcode className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
                <Input value={scan} onChange={(event) => setScan(event.target.value)} placeholder="Asset tag or serial number" className="pl-9 font-mono" autoComplete="off" />
              </div>
            </Field>
          </form>
          {scanned && <p className={cn('mt-2 text-sm', scanned.ok ? 'text-emerald-700' : 'text-amber-700')}>{scanned.text}</p>}
        </Card>

        <Card>
          <CardHeader title="Check in" description={selected.size > 0 ? `${selected.size} selected` : 'Select or scan devices first'} />
          <div className="space-y-4 p-4">
            <Field label="Returned condition">
              <Select value={condition} onChange={(event) => setCondition(event.target.value as AssetCondition | '')}>
                <option value="">Not inspected</option>
                {Object.entries(CONDITION_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Then mark them">
              <Select value={assetStatus} onChange={(event) => setAssetStatus(event.target.value)}>
                <option value="AVAILABLE">Available</option>
                <option value="IN_REPAIR">In repair</option>
                <option value="LOST">Lost</option>
                <option value="STOLEN">Stolen</option>
                <option value="RETIRED">Retired</option>
              </Select>
            </Field>
            <Field label="Notes">
              <Textarea rows={2} value={notes} maxLength={2000} onChange={(event) => setNotes(event.target.value)} />
            </Field>
            <p className="text-xs text-slate-500">Batch check-ins skip the check-in form and photos.</p>
            <Button variant="primary" className="w-full" icon={<Undo2 />} disabled={selected.size === 0} onClick={() => setConfirming(true)}>
              Check in {plural(selected.size, 'device')}
            </Button>
          </div>
        </Card>

        {result && (
          <div className="space-y-3">
            {result.checkedIn.length > 0 && (
              <Alert tone="green" title={`Checked in ${plural(result.checkedIn.length, 'device')}`}>
                {late > 0 ? `${late} came back late.` : 'All of them were on time.'}
              </Alert>
            )}
            {result.failed.length > 0 && (
              <Alert tone="red" title={`${result.failed.length} couldn’t be checked in`}>
                <ul className="mt-1 space-y-0.5">
                  {result.failed.map((row) => (
                    <li key={row.checkoutId}>{row.error}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => collect.mutate()}
        loading={collect.isPending}
        tone="primary"
        confirmLabel="Check in"
        title={`Check in ${plural(selected.size, 'device')}?`}
        description={`They’ll be marked ${assetStatus === 'AVAILABLE' ? 'available' : assetStatus.replace('_', ' ').toLowerCase()}${condition ? `, in ${CONDITION_LABEL[condition].toLowerCase()} condition` : ''}.`}
      />
    </div>
  );
}
